<script lang="ts">
  import { type Currency, type Period, type Product } from "$lib/products";
  import { m } from "$lib/paraglide/messages";
  import Icon from "@brave/leo/src/components/icon/icon.svelte";
  import RadioButton from "@brave/leo/src/components/radioButton/radioButton.svelte";
  import DOMPurify from "isomorphic-dompurify";
  import { onMount } from "svelte";
  import { fade } from "svelte/transition";
  import Logo from "./ProductLogo.svelte";
  import UnsupportedSystem from "./UnsupportedSystem.svelte";
  import PriceOptionContent from "./PriceOptionContent.svelte";
  import { isLinux } from "$lib/utils";
  import OriginLinuxBanner from "./OriginLinuxBanner.svelte";

  export let product: Product;
  export let selectedPeriod: Period = "month";

  // Note that while not currently in use, this is being left for the time when we support
  // displaying prices in different fiat currencies
  export let selectedCurrency: Currency = "USD";

  const { name, baseName, description, features, prices, hasFreeVersion, trialDays } = product;

  onMount(() => {
    document.body.classList.add("product-landing");

    return () => {
      document.body.classList.remove("product-landing");
    };
  });

  $: priceOptions = [...prices.values()]
    .filter((price) => price.currency === selectedCurrency)
    .sort((a, b) => {
      if (a.period === "year" && b.period !== "year") {
        return -1;
      } else {
        return 0;
      }
    });

  $: {
    if (priceOptions.length < 2) {
      selectedPeriod = priceOptions.at(0).period;
    }
  }
</script>

<main
  class="box relative overflow-hidden rounded-none p-0 before:absolute before:left-0 before:top-0 before:z-0 before:block before:h-[299px] before:w-full before:-translate-y-3/4 before:rounded-[720px] before:bg-neutral-10 before:blur-[100px] md:max-w-[720px]"
  in:fade={{ duration: 600 }}
>
  <section
    class="box overflow-hidden rounded-none border-b border-divider-subtle shadow-none max-sm:p-xl max-sm:pt-2xl"
  >
    <header class="relative z-10 flex items-center justify-center gap-2xl pb-m">
      <Logo {name} />
      <div class="flex flex-col justify-center gap-s">
        <h1 class="text-h2">{baseName}</h1>
        {#if hasFreeVersion}
          <p class="text-large-regular">{m.premium_product_label()}</p>
        {/if}
      </div>
    </header>
    <div
      class="relative z-10 flex flex-col gap-xl p-2xl text-text-secondary sm:gap-3xl sm:pb-6xl sm:pt-2xl"
    >
      <div class="-last:[&>p]:pb-m">{@html DOMPurify.sanitize(description, { ALLOWED_TAGS: ['p', 'span'], RETURN_TRUSTED_TYPE: true })}</div>
      <ul class="flex flex-col gap-s" style="--leo-icon-color: var(--leo-color-primary-40)">
        {#each features as feature}
          <li class="flex gap-m">
            <Icon name="check-normal" /> <span>{@html DOMPurify.sanitize(feature, { RETURN_TRUSTED_TYPE: true })}</span>
          </li>
        {/each}
        {#if selectedPeriod !== "one-off"}
          <li class="flex gap-m"><Icon name="check-normal" /> {m.cancel_anytime_feature()}</li>
        {/if}
        {#if trialDays > 0}
          <li class="flex gap-m font-semibold text-primary-40">
            <Icon name="check-normal" />
            {m.free_trial_days_feature({ trialDays })}
          </li>
        {/if}
      </ul>
    </div>

    <div class="overflow-clip rounded-m border border-divider-subtle/40 grid grid-cols-[max-content_var(--leo-spacing-m)_1fr] sm:grid-cols-[max-content_var(--leo-spacing-m)_1fr_var(--leo-spacing-xl)_fit-content(100%)]">
      {#each priceOptions as option}
        <div class="price-option border-divider-subtle/40 px-xl py-l text-text-tertiary -first:border-t col-span-full grid grid-cols-subgrid gap-y-s items-center">
          {#if priceOptions.length > 1}
            <RadioButton
              isDisabled={!product.browserSupport.isSupported}
              name="payment-term"
              size="normal"
              bind:currentValue={selectedPeriod}
              value={option.period}
            >
              <PriceOptionContent {option} />
            </RadioButton>
          {:else}
            <PriceOptionContent {option} isSolo={true} />
          {/if}
        </div>
      {/each}
    </div>
  </section>
  {#if product.browserSupport.isSupported}
    <section class="box rounded-none shadow-none max-sm:p-2xl -last:border-b -last:border-divider-subtle">
      <slot />
    </section>
  {:else}
    <section class="md:box rounded-none shadow-none md:p-xl">
      <div class="overflow-hidden border-divider-subtle md:rounded-m md:border">
        <UnsupportedSystem {product}>
          {product.browserSupport.message}
        </UnsupportedSystem>
      </div>
    </section>
  {/if}
  {#if isLinux() && product.name === "origin"}
    <OriginLinuxBanner />
  {/if}
</main>

<style>
  @supports (grid-template-columns: subgrid) {
    .price-option :global(.leo-radiobutton) {
      display: contents !important;
    }
}
</style>
